/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, TextField, Button, Switch, Divider } from '@components/atoms'
import {
	FullModalLayout,
	SeasonOpenConfirmationModal,
	SeasonOpenPeriodWarningModal,
	SeasonQueueOrderWarningModal,
} from '@components/molecules'

import { FormControlLabel, InputAdornment, Chip } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AttachMoney, CalendarToday } from '@mui/icons-material'

import { useAppDispatch, useAppSelector, routeActions } from '@redux/index'
import { seasonActions } from '@redux/store/index'
import { SeasonType, SeasonModificationType } from '@schemas'

import { useParams } from 'react-router-dom'
import { green, grey } from '@mui/material/colors'
import dayjs from 'dayjs'

/**
 * 시즌 수정 + 판매 시작.
 *
 * 모달이 아니라 라우트를 가진 전체 화면이다. 경고 모달을 그 위에 겹쳐 띄워야 해서
 * 좁은 모달로는 부족했다. 권한은 menu.config 의 sidePageList('seasonEdit')에서
 * 부모(/main/season)를 따른다.
 */
const SeasonEditPage = () => {
	// hook
	const { seasonId } = useParams()

	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const seasonList: SeasonType[] = useAppSelector(state => state.season.seasonList)
	const openError: string | null = useAppSelector(state => state.season.openError)

	// variables
	const thisSeason: SeasonType | undefined = React.useMemo(
		() => seasonList.find(aSeason => aSeason.id === seasonId),
		[seasonList, seasonId],
	)
	// 연도 필터와 무관하게 유지되는 판매중 시즌(전환 확인 모달의 "현재")
	const openSeason: SeasonType | null = useAppSelector(state => state.season.openSeason)
	/** 대기열 (queueOrder 오름차순). 순서 경고 판정과 추가 위치 계산에 쓴다. */
	const queueList: SeasonType[] = React.useMemo(
		() =>
			seasonList
				.filter(aSeason => aSeason.queueOrder !== null && aSeason.openYn !== 1)
				.sort((a, b) => (a.queueOrder ?? 0) - (b.queueOrder ?? 0)),
		[seasonList],
	)

	// state
	const [seasonData, setSeasonData] = React.useState<SeasonModificationType>({
		title: '',
		startDate: '',
		endDate: '',
		activeYn: 0,
		targetUsd: 0,
		targetQuantity: 0,
	})
	const [errors, setErrors] = React.useState<{ [key: string]: string }>({})
	const [showOpenConfirm, setShowOpenConfirm] = React.useState<boolean>(false)
	const [showPeriodWarning, setShowPeriodWarning] = React.useState<boolean>(false)
	// 대기열 추가 시 날짜 역전이 감지되면 확인을 받는다
	const [queueConflict, setQueueConflict] = React.useState<SeasonType | null>(null)

	const onCloseClick = React.useCallback(() => {
		reduxDispatch(routeActions.NAVIGATE_PATH('/main/season'))
	}, [])

	// 새로고침으로 직접 들어오면 목록이 비어 있다
	React.useEffect(() => {
		if (seasonList.length === 0) {
			reduxDispatch(seasonActions.TRIGGER_INIT_SEASON_PAGE(true))
		}
	}, [])

	React.useEffect(() => {
		if (thisSeason) {
			setSeasonData({
				title: thisSeason.title,
				startDate: thisSeason.startDate,
				endDate: thisSeason.endDate,
				activeYn: thisSeason.activeYn,
				targetUsd: thisSeason.targetUsd,
				targetQuantity: thisSeason.targetQuantity,
			})
		}
	}, [thisSeason])

	// 서버가 기간 밖이라고 거부한 경우에도 경고를 띄운다.
	// 클라이언트 검사를 통과했더라도 날짜가 바뀌었을 수 있다.
	React.useEffect(() => {
		if (openError === 'SEASON_NOT_IN_SALE_PERIOD') {
			setShowOpenConfirm(false)
			setShowPeriodWarning(true)
		}
	}, [openError])

	const isOpen: boolean = thisSeason?.openYn === 1
	const isInSalePeriod: boolean = React.useMemo(() => {
		if (!seasonData.startDate || !seasonData.endDate) {
			return false
		}
		// 날짜 단위 비교. 시작일·종료일 당일을 모두 포함한다.
		const today = dayjs().startOf('day')
		const start = dayjs(seasonData.startDate).startOf('day')
		const end = dayjs(seasonData.endDate).startOf('day')

		return !today.isBefore(start) && !today.isAfter(end)
	}, [seasonData.startDate, seasonData.endDate])

	/** 종료일이 지난 시즌. 대기열에 넣을 이유가 없으므로 버튼을 막는다. */
	const isExpired: boolean = React.useMemo(() => {
		if (!seasonData.endDate) {
			return false
		}

		return dayjs().startOf('day').isAfter(dayjs(seasonData.endDate).startOf('day'))
	}, [seasonData.endDate])

	const isInQueue: boolean =
		thisSeason?.queueOrder !== null && thisSeason?.queueOrder !== undefined

	const validateForm = () => {
		const newErrors: { [key: string]: string } = {}
		if (!seasonData.title.trim()) newErrors.title = '시즌 제목을 입력해주세요.'
		if (!seasonData.startDate) newErrors.startDate = '시작 날짜를 선택해주세요.'
		if (!seasonData.endDate) newErrors.endDate = '종료 날짜를 선택해주세요.'
		if (seasonData.targetUsd <= 0)
			newErrors.targetUsd = '유효한 목표 금액을 입력해주세요.'
		if (seasonData.targetQuantity <= 0)
			newErrors.targetQuantity = '유효한 목표 수량을 입력해주세요.'
		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const onInputChange = (
		field: keyof SeasonModificationType,
		value: string | number | boolean,
	) => {
		setSeasonData(prev => ({ ...prev, [field]: value }))
	}

	const onModifyClick = () => {
		if (!validateForm()) {
			return
		}

		reduxDispatch(
			seasonActions.UPDATE_SEASON({
				id: thisSeason?.id ?? '',
				season: {
					...seasonData,
					startDate: dayjs(seasonData.startDate).format('YYYY-MM-DD'),
					endDate: dayjs(seasonData.endDate).format('YYYY-MM-DD'),
				},
			}),
		)
		onCloseClick()
	}

	const onOpenClick = () => {
		reduxDispatch(seasonActions.CLEAR_OPEN_ERROR())

		// 기간 밖이면 서버까지 가지 않고 여기서 막는다. 강행 경로는 없다.
		if (!isInSalePeriod) {
			setShowPeriodWarning(true)
			return
		}

		setShowOpenConfirm(true)
	}

	const onOpenConfirm = () => {
		reduxDispatch(seasonActions.OPEN_SEASON(thisSeason?.id ?? ''))
		setShowOpenConfirm(false)
		onCloseClick()
	}

	const onCloseSaleClick = () => {
		reduxDispatch(seasonActions.CLOSE_SEASON())
		onCloseClick()
	}

	/**
	 * 대기열 추가.
	 *
	 * 앞 순번에 더 늦게 시작하는 시즌이 있으면(날짜 역전) 확인을 받는다.
	 * 판정은 Season.page 의 목록 쪽과 같다. (SEASON_OPEN_PLAN.md §7.1)
	 */
	const onAddToQueueClick = () => {
		if (thisSeason === undefined) {
			return
		}

		const conflict = queueList.find(anExisting =>
			dayjs(anExisting.startDate).isAfter(dayjs(thisSeason.startDate)),
		)

		if (conflict) {
			setQueueConflict(conflict)
			return
		}

		reduxDispatch(
			seasonActions.UPDATE_SEASON_QUEUE([
				...queueList.map(aSeason => aSeason.id),
				thisSeason.id,
			]),
		)
	}

	/** 경고에도 그대로 맨 뒤에 추가 */
	const onQueueKeepOrder = () => {
		if (thisSeason === undefined) {
			return
		}

		reduxDispatch(
			seasonActions.UPDATE_SEASON_QUEUE([
				...queueList.map(aSeason => aSeason.id),
				thisSeason.id,
			]),
		)
		setQueueConflict(null)
	}

	/** 시작일 기준으로 올바른 위치에 삽입 */
	const onQueueMoveForward = () => {
		if (thisSeason === undefined) {
			return
		}

		const insertIdx = queueList.findIndex(anExisting =>
			dayjs(anExisting.startDate).isAfter(dayjs(thisSeason.startDate)),
		)
		const newList = [...queueList]
		newList.splice(insertIdx === -1 ? newList.length : insertIdx, 0, thisSeason)

		reduxDispatch(seasonActions.UPDATE_SEASON_QUEUE(newList.map(aSeason => aSeason.id)))
		setQueueConflict(null)
	}

	return (
		<View css={style}>
			<FullModalLayout
				title="시즌 수정"
				onCloseClick={onCloseClick}
				titleStyle={{ marginBottom: '1rem' }}
			>
				<View className="seasonEditOuterView">
					<View className="fieldView">
						<TextField
							label="시즌 제목"
							value={seasonData.title}
							onChange={e => onInputChange('title', e.target.value)}
							fullWidth
							error={!!errors.title}
							helperText={errors.title}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<CalendarToday />
									</InputAdornment>
								),
							}}
						/>
					</View>

					<LocalizationProvider dateAdapter={AdapterDateFns}>
						<View className="dateFieldView">
							<View className="dateField">
								<DatePicker
									label="시작 날짜"
									value={seasonData.startDate ? new Date(seasonData.startDate) : null}
									onChange={date =>
										onInputChange('startDate', date ? date.toISOString() : '')
									}
									format="yyyy-MM-dd"
								/>
							</View>
							<View className="dateField">
								<DatePicker
									label="종료 날짜"
									value={seasonData.endDate ? new Date(seasonData.endDate) : null}
									onChange={date =>
										onInputChange('endDate', date ? date.toISOString() : '')
									}
									format="yyyy-MM-dd"
								/>
							</View>
						</View>
					</LocalizationProvider>

					<View className="fieldView">
						<TextField
							label="목표 금액 (USD)"
							value={seasonData.targetUsd}
							onChange={e => onInputChange('targetUsd', Number(e.target.value))}
							fullWidth
							type="number"
							error={!!errors.targetUsd}
							helperText={errors.targetUsd}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<AttachMoney />
									</InputAdornment>
								),
							}}
						/>
					</View>

					<View className="fieldView">
						<TextField
							label="목표 수량"
							value={seasonData.targetQuantity}
							onChange={e => onInputChange('targetQuantity', Number(e.target.value))}
							fullWidth
							type="number"
							error={!!errors.targetQuantity}
							helperText={errors.targetQuantity}
						/>
					</View>

					<View className="switchView">
						<FormControlLabel
							control={
								<Switch
									checked={seasonData.activeYn === 1}
									onChange={e => onInputChange('activeYn', e.target.checked ? 1 : 0)}
									color="primary"
								/>
							}
							label="활성화"
						/>
					</View>

					<View className="bottomView">
						<Button variant="contained" onClick={onModifyClick}>
							수정하기
						</Button>
					</View>

					<Divider />

					<View className="saleSectionView">
						<View className="saleHeaderView">
							<Text className="saleTitle">판매 상태</Text>
							{isOpen && <Chip label="판매중" size="small" className="openChip" />}
						</View>

						<Text className="saleDescription">
							{isOpen
								? '이 시즌이 지금 shop 에 노출되고 있습니다. 판매를 중지하면 shop 의 전 상품이 Sold Out 으로 표시됩니다.'
								: '이 시즌을 shop 에 노출합니다. 판매중인 시즌은 항상 하나이며, 다른 시즌이 판매중이면 자동으로 중지됩니다.'}
						</Text>

						{!isOpen && !isInSalePeriod && (
							<Text className="salePeriodNotice">
								{`오늘(${dayjs().format(
									'YYYY-MM-DD',
								)})은 이 시즌의 판매 기간이 아닙니다. 기간 안에서만 판매를 시작할 수 있습니다.`}
							</Text>
						)}

						<View className="bottomView">
							{!isOpen && (
								<Button
									variant="outlined"
									color="primary"
									disabled={isExpired || isInQueue}
									onClick={onAddToQueueClick}
								>
									{isInQueue ? '대기열 등록됨' : '대기열 등록'}
								</Button>
							)}

							{isOpen ? (
								<Button variant="outlined" color="inherit" onClick={onCloseSaleClick}>
									판매 중지
								</Button>
							) : (
								<Button
									variant="contained"
									color="success"
									disabled={!isInSalePeriod}
									onClick={onOpenClick}
								>
									판매 시작
								</Button>
							)}
						</View>
					</View>
				</View>
			</FullModalLayout>

			<SeasonOpenConfirmationModal
				open={showOpenConfirm}
				currentSeason={openSeason}
				targetSeason={thisSeason ?? null}
				onCancel={() => setShowOpenConfirm(false)}
				onConfirm={onOpenConfirm}
			/>

			<SeasonQueueOrderWarningModal
				open={queueConflict !== null}
				targetSeason={thisSeason ?? null}
				conflictSeason={queueConflict}
				onKeepOrder={onQueueKeepOrder}
				onMoveForward={onQueueMoveForward}
				onClose={() => setQueueConflict(null)}
			/>

			<SeasonOpenPeriodWarningModal
				open={showPeriodWarning}
				season={thisSeason ?? null}
				onClose={() => {
					setShowPeriodWarning(false)
					reduxDispatch(seasonActions.CLEAR_OPEN_ERROR())
				}}
			/>
		</View>
	)
}

export default SeasonEditPage

const style = css({
	'.seasonEditOuterView': {
		position: 'relative',
		height: 'fit-contents',
		overflow: 'auto',
		textAlign: 'left',
		// TextField 의 라벨이 위로 떠오르므로 여유가 없으면 잘린다
		paddingTop: '0.75rem',

		'.fieldView': {
			marginBottom: '1.5rem',
		},

		'.dateFieldView': {
			display: 'flex',
			gap: '1rem',
			marginBottom: '1.5rem',

			'.dateField': {
				flex: 1,
			},
		},

		'.switchView': {
			marginTop: '1rem',
		},

		'.bottomView': {
			display: 'flex',
			justifyContent: 'right',
			alignItems: 'center',
			gap: '0.5rem',
			width: '100%',
			marginTop: '1.5rem',
			marginBottom: '3rem',
		},

		'.saleSectionView': {
			marginTop: '3rem',

			'.saleHeaderView': {
				display: 'flex',
				alignItems: 'center',
				gap: '0.5rem',
				marginBottom: '0.5rem',

				'.saleTitle': {
					margin: 0,
					fontSize: '1.1rem',
					fontWeight: 700,
				},

				'.openChip': {
					background: green['A700'],
					color: 'white',
					fontWeight: 900,
					fontSize: '0.75rem',
				},
			},

			'.saleDescription': {
				display: 'block',
				margin: 0,
				color: grey[700],
				lineHeight: 1.7,
			},

			'.salePeriodNotice': {
				display: 'block',
				margin: '1rem 0 0',
				padding: '0.75rem',
				background: grey[100],
				borderRadius: '4px',
				color: grey[800],
				lineHeight: 1.6,
			},
		},
	},
})
